import React from "react";
import { motion } from "framer-motion";
import { Plug, Zap, CheckCircle2, ArrowUpRight } from "lucide-react";
import { Reveal, EASE, BOOK_DEMO_URL } from "../../lib/motion";

const STEPS = [
  { n: "01", icon: Plug, title: "Connect your data", desc: "Plug in your billing, POS and inventory. No migration headaches, no IT team required." },
  { n: "02", icon: Zap, title: "Get instant insights", desc: "ForesightFlow reads the patterns and surfaces what matters — in plain language." },
  { n: "03", icon: CheckCircle2, title: "Make confident decisions", desc: "Act on discounts, restocking and bundles knowing the numbers have your back." },
];

const HowItWorks = () => {
  return (
    <section
      data-testid="how-it-works-section"
      className="relative py-24 md:py-32 lg:py-40 bg-[#3B2B21] text-[#FAF8F5] overflow-hidden"
    >
      {/* thin taupe gridlines motif */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right,#B39B77 1px,transparent 1px),linear-gradient(to bottom,#B39B77 1px,transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
        <Reveal>
          <p className="font-body text-xs uppercase tracking-[0.24em] text-[#B39B77] mb-5">
            03 / How it works
          </p>
          <h2 className="font-display font-semibold text-[#FAF8F5] text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-3xl">
            From scattered spreadsheets to{" "}
            <span className="italic text-[#B39B77]">confident calls</span> — in three steps.
          </h2>
        </Reveal>

        <div className="relative mt-20 md:mt-28">
          {/* animated connecting line */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: EASE }}
            style={{ transformOrigin: "left" }}
            className="hidden md:block absolute top-9 left-0 right-0 h-px bg-gradient-to-r from-[#B39B77] via-[#B39B77]/50 to-transparent"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-10">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, ease: EASE, delay: i * 0.18 }}
                  data-testid={`step-${i}`}
                  className="group relative"
                >
                  {/* icon node sits on the line */}
                  <div className="relative z-10 flex items-center justify-center w-[72px] h-[72px] rounded-full border border-[#B39B77]/40 bg-[#3B2B21] transition-colors duration-500 group-hover:border-[#B39B77] group-hover:bg-[#B39B77]/10">
                    <Icon className="w-7 h-7 text-[#B39B77]" strokeWidth={1.4} />
                  </div>

                  <span className="mt-8 block font-display text-5xl md:text-6xl leading-none text-[#B39B77]/25 transition-colors duration-500 group-hover:text-[#B39B77]/60">
                    {s.n}
                  </span>

                  <h3 className="mt-4 font-display text-2xl md:text-3xl text-[#FAF8F5]">
                    {s.title}
                  </h3>
                  <p className="mt-4 max-w-xs font-body text-base leading-relaxed text-[#FAF8F5]/65">
                    {s.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.2}>
          <div className="mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-[#FAF8F5]/10 pt-10">
            <p className="font-display text-xl md:text-2xl text-[#FAF8F5]/90 max-w-lg">
              That&apos;s it. No dashboards to babysit, no analyst to hire.
            </p>
            <a
              href={BOOK_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="how-book-demo"
              className="group inline-flex items-center gap-2 rounded-full bg-[#B39B77] px-7 py-4 font-body text-base font-medium text-[#1C1C1E] transition-colors duration-300 hover:bg-[#96805E]"
            >
              See it on your data
              <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default HowItWorks;
